import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  FormControlLabel,
  SwitchCheckbox,
  Stack,
  Collapse,
} from 'views/components';
import {Translate, translate, DatePickerYearMonth} from 'views/module';
import {ExpandMore, Delete, Add} from 'views/icons';

import moment from 'moment';

const WorkHistorySection = (props) => {
  const {onWorkHistoryChange, workHistory = [], setValidity} = props;

  // Validate section based on work history entries
  React.useEffect(() => {
    const isValid = workHistory.length > 0;
    setValidity(isValid);
  }, [workHistory, setValidity]);

  // Generate unique ID for new entries
  const generateId = () => `work_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  // Add new employment entry
  const handleAddEmployer = () => {
    const newEntry = {
      id: generateId(),
      employerName: '',
      startDate: '',
      endDate: '',
      canContact: false,
      referenceContactInfo: '',
    };

    const updatedHistory = [...workHistory, newEntry];

    if (onWorkHistoryChange) {
      onWorkHistoryChange(updatedHistory);
    }
  };

  // Update an existing entry
  const handleUpdateEntry = (id, field, value) => {
    const updatedHistory = workHistory.map((entry) =>
      entry.id === id ? {...entry, [field]: value} : entry,
    );

    if (onWorkHistoryChange) {
      onWorkHistoryChange(updatedHistory);
    }
  };

  // Delete an entry
  const handleDeleteEntry = (id) => {
    const updatedHistory = workHistory.filter((entry) => entry.id !== id);

    if (onWorkHistoryChange) {
      onWorkHistoryChange(updatedHistory);
    }
  };

  // Format date range for display
  const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return translate('caregiverScreening.WorkHistory.DatesNotSpecified');

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const [year, month] = dateStr.split('-');
      const monthNames = translate('caregiverScreening.WorkHistory.DateFormats.MonthNames');
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    };

    const start = formatDate(startDate);
    const end = formatDate(endDate);

    if (start && end) return `${start} - ${end}`;
    if (start) return `${start} - ${translate('caregiverScreening.WorkHistory.Present')}`;
    if (end) return translate('caregiverScreening.WorkHistory.UntilDate').replace('#{date}', end);
    return translate('caregiverScreening.WorkHistory.DatesNotSpecified');
  };

  return (
    <Box sx={{p: 3}}>
      <Typography variant="h5" gutterBottom align="center" fontWeight={600}>
        <Translate expression='caregiverScreening.WorkHistory.Title'/>
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{mb: 3}}>
        <Translate expression='caregiverScreening.WorkHistory.Subtitle'/>
      </Typography>

      {/* Employment History Accordions */}
      <Box sx={{mb: 3}}>
        {
          workHistory.map((entry, index) => (
            <Accordion key={entry.id} sx={{mb: 1}} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  'backgroundColor': 'action.hover',
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    pr: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" component="div">
                      {entry.employerName || translate('caregiverScreening.WorkHistory.EmploymentLabel', {number: index + 1})}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateRange(entry.startDate, entry.endDate)}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                <Stack spacing={3}>
                  {/* Employer Name Field */}
                  <TextField
                    label={translate('caregiverScreening.WorkHistory.EmployerNameLabel')}
                    value={entry.employerName}
                    onChange={(text) => handleUpdateEntry(entry.id, 'employerName', text)}
                    fullWidth
                    required
                    helperText={translate('caregiverScreening.WorkHistory.EmployerNameHelper')}
                  />

                  {/* Date Fields */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                      gap: 2,
                    }}
                  >
                    <DatePickerYearMonth
                      label={translate('caregiverScreening.WorkHistory.StartDateLabel')}
                      disableFuture
                      maxDate={entry.endDate && entry.endDate.trim() ? moment(entry.endDate + '-01').endOf('month') : undefined}
                      value={entry.startDate && entry.startDate.trim() ? moment(entry.startDate + '-01') : null}
                      onChange={(newValue) => {
                        const dateString = newValue && newValue.isValid() ? newValue.format('YYYY-MM') : '';
                        handleUpdateEntry(entry.id, 'startDate', dateString);
                      }}
                      textFieldProps={{
                        helperText: translate('caregiverScreening.WorkHistory.WhenStartedHelper'),
                        fullWidth: true,
                      }}
                    />

                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                      <DatePickerYearMonth
                        label={translate('caregiverScreening.WorkHistory.EndDateLabel')}
                        disableFuture
                        minDate={entry.startDate && entry.startDate.trim() ? moment(entry.startDate + '-01') : undefined}
                        value={entry.endDate && entry.endDate.trim() ? moment(entry.endDate + '-01') : null}
                        onChange={(newValue) => {
                          const dateString = newValue && newValue.isValid() ? newValue.format('YYYY-MM') : '';
                          handleUpdateEntry(entry.id, 'endDate', dateString);
                        }}
                        textFieldProps={{
                          helperText: translate('caregiverScreening.WorkHistory.LeaveBlankHelper'),
                          fullWidth: true,
                        }}
                      />
                      {
                        entry.endDate && entry.endDate.trim() && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleUpdateEntry(entry.id, 'endDate', '')}
                            sx={{alignSelf: 'flex-start', fontSize: '0.75rem'}}
                          >
                            <Translate expression='caregiverScreening.WorkHistory.ClearEndDateButton'/>
                          </Button>
                        )
                      }
                    </Box>
                  </Box>

                  {/* Reference Contact Permission */}
                  <FormControlLabel
                    control={
                      <SwitchCheckbox
                        value={entry.canContact ?? false}
                        onChange={(checked) => handleUpdateEntry(entry.id, 'canContact', checked)}
                        color="primary"
                      />
                    }
                    label={translate('caregiverScreening.WorkHistory.CanContactReference')}
                  />

                  {/* Reference Contact Information */}
                  <Collapse in={entry.canContact ?? false}>
                    <TextField
                      label={translate('caregiverScreening.WorkHistory.ReferenceContactInfo')}
                      value={entry.referenceContactInfo || ''}
                      onChange={(text) => handleUpdateEntry(entry.id, 'referenceContactInfo', text)}
                      fullWidth
                      rows={2}
                      helperText={translate('caregiverScreening.WorkHistory.ReferenceContactInfoHelper')}
                      placeholder={translate('caregiverScreening.WorkHistory.ReferenceContactInfoPlaceholder')}
                    />
                  </Collapse>

                  {/* Delete Button */}
                  <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDeleteEntry(entry.id)}
                      size="small"
                    >
                      <Translate expression='caregiverScreening.WorkHistory.RemoveEmploymentButton'/>
                    </Button>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))
        }
      </Box>

      {/* Add Employer Button */}
      <Box sx={{display: 'flex', justifyContent: 'center'}}>
        <Button
          variant="outlined"
          onClick={handleAddEmployer}
          startIcon={<Add />}
          sx={{
            'borderStyle': 'dashed',
            'borderWidth': 2,
            'py': 1.5,
            'px': 3,
            '&:hover': {
              borderStyle: 'dashed',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Translate expression='caregiverScreening.WorkHistory.AddEmployerButton'/>
        </Button>
      </Box>

      {/* Summary */}
      <Collapse in={workHistory.length > 0}>
        <Box sx={{
          mt: 3,
          p: 2,
          backgroundColor: 'action.hover',
          borderRadius: 1,
        }}
        >
          <Typography variant="h6" gutterBottom>
            <Translate
              expression='caregiverScreening.WorkHistory.SummaryTitle'
              substitute={{
                count: workHistory.length,
                positions: workHistory.length === 1 ?
                  translate('caregiverScreening.WorkHistory.PositionSingular') :
                  translate('caregiverScreening.WorkHistory.PositionPlural'),
              }}
            />
          </Typography>
          <Box component="ul" sx={{pl: 2, m: 0}}>
            {
              workHistory.map((entry) => (
                <Typography component="li" key={entry.id} variant="body2">
                  <strong>{entry.employerName || translate('caregiverScreening.WorkHistory.UnnamedEmployer')}</strong>
                  {' • '}
                  {formatDateRange(entry.startDate, entry.endDate)}
                  {entry.canContact && ` • ${translate('caregiverScreening.WorkHistory.ReferenceAvailable')}`}
                </Typography>
              ))
            }
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

WorkHistorySection.propTypes = {
  onWorkHistoryChange: PropTypes.func,
  setValidity: PropTypes.func.isRequired,
  workHistory: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    employerName: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    canContact: PropTypes.bool.isRequired,
    referenceContactInfo: PropTypes.string,
  })),
};

export default WorkHistorySection;
